/** @file Follows the compiled store: a new rig gets a scene, page table and ghost; images load meanwhile. */
import { compiledStore } from '../../stores/compiledStore';
import type { RuntimeState } from './runtimeState';
import { createScene } from './scene/createScene';
import { loadPages } from './scene/loadPages';
import { pageSet } from './scene/pageSet';
import { sceneMaps } from './scene/sceneMaps';
import { syncGhost } from './syncGhost';
import { viewportStore } from './viewportStore';

/** Rebuilds the page table from the image cache and uploads it; counts missing images in the notice. */
function refreshPages(s: RuntimeState): void {
  if (s.scene === null) return;
  s.pages = pageSet(s.scene.data, s.images, s.deps.font, s.deps.fontImage);
  s.deps.renderer.uploadPages(s.pages.textures, s.pages.images);
  if (s.pages.missing > 0) viewportStore.setState({ notice: { kind: 'info', key: 'viewport.missingTexture', params: { count: s.pages.missing } } });
  s.dirty = true;
}

/** Called on every compiled-store change and once at start; a new rig rebuilds the ghost over its page table. */
export function syncScene(s: RuntimeState): void {
  const { data, doc, error } = compiledStore.getState();
  s.dirty = true;
  if (data === null || doc === null) {
    Object.assign(s, { scene: null, maps: null, doc: null, pages: null, ghost: null });
    return;
  }
  if (error !== null) viewportStore.setState({ notice: { kind: 'info', key: 'viewport.compileError' } });
  s.doc = doc;
  s.maps = sceneMaps(doc, data);
  if (s.scene?.data === data) return;
  s.scene = createScene(data);
  refreshPages(s);
  syncGhost(s);
  void loadPages(data.pages.map((p) => p.id), s.deps.fetchPng, s.images).then((changed) => {
    if (changed && s.scene?.data === data) refreshPages(s);
  });
}
